'use client'

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot, $isParagraphNode } from 'lexical'
import { $isMentionNode } from './MentionNode'
import { serializeEditorState } from './SerializationPlugin'

/**
 * RestoreCaretPlugin — given a caret offset expressed against the
 * serialized script (the same coordinate system tracked by
 * CursorContextPlugin), positions the Lexical selection at the
 * equivalent location once on mount.
 *
 * Used to maintain the user's editing position after the editor is
 * remounted in response to an external script reload (e.g. the
 * scripting agent applying a diff).
 */

function mentionEndPos(serialized, start) {
    let depth = 0
    for (let i = start; i < serialized.length; i++) {
        if (serialized[i] === '{') depth++
        else if (serialized[i] === '}') {
            depth--
            if (depth === 0) return i + 1
        }
    }
    return serialized.length
}

function childLen(child, serialized, pos) {
    return $isMentionNode(child)
        ? mentionEndPos(serialized, pos) - pos
        : child.getTextContent().length
}

export default function RestoreCaretPlugin({ index }) {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        if (index == null) return

        const raf = requestAnimationFrame(() => {
            editor.update(() => {
                const serialized = serializeEditorState(editor.getEditorState())
                const target = Math.min(Math.max(0, index), serialized.length)

                const root = $getRoot()
                const paragraphs = root.getChildren().filter($isParagraphNode)
                if (paragraphs.length === 0) return

                let pos = 0
                for (let p = 0; p < paragraphs.length; p++) {
                    if (p > 0) pos += 1
                    const paragraph = paragraphs[p]
                    const children = paragraph.getChildren()

                    if (children.length === 0) {
                        if (target <= pos) {
                            paragraph.select(0, 0)
                            return
                        }
                        continue
                    }

                    for (let c = 0; c < children.length; c++) {
                        const child = children[c]
                        const len = childLen(child, serialized, pos)

                        if (target <= pos + len) {
                            if ($isMentionNode(child)) {
                                const idx = c + (target === pos ? 0 : 1)
                                paragraph.select(idx, idx)
                            } else {
                                const offset = target - pos
                                child.select(offset, offset)
                            }
                            return
                        }
                        pos += len
                    }
                }

                const last = paragraphs[paragraphs.length - 1]
                if (last) last.select(last.getChildrenSize(), last.getChildrenSize())
            }, { tag: 'restore-caret' })
        })

        return () => cancelAnimationFrame(raf)
    }, [editor, index])

    return null
}
